import React, { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';

interface AutocompleteListProps<T> {
  options: T[];
  isLoading?: boolean;
  isSelected: (option: T) => boolean;
  getLabel: (option: T) => string;
  onSelect: (option: T) => void;
  renderOption?: (option: T, isSelected: boolean) => React.ReactNode;
}

export default function AutocompleteList<T>({
  options,
  isSelected,
  getLabel,
  renderOption,
  onSelect,
  isLoading = false,
}: AutocompleteListProps<T>) {
  // HOOKS
  const parentRef = useRef<HTMLDivElement>(null);

  // VIRTUALIZER
  const rowVirtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    overscan: 6,
  });

  // RENDER
  return (
    <div ref={parentRef} className="max-h-[200px] overflow-y-auto relative">
      {isLoading && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin text-[#0C8DBF]" />
        </div>
      )}

      {!isLoading && options.length === 0 && (
        <div className="flex justify-center py-4 text-[14px] text-[#9D9D9C]">No options found</div>
      )}

      {!isLoading && options.length > 0 && (
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            position: 'relative',
            width: '100%',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const option = options[virtualRow.index];
            const checked = isSelected(option);

            return (
              <div
                className={cn(
                  'flex items-center justify-between cursor-pointer p-2 hover:bg-[#E4F8FF]',
                  checked && 'bg-[#E4F8FF]'
                )}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                }}
                key={String(virtualRow.index + 1)}
                data-index={String(virtualRow.index)}
                ref={rowVirtualizer.measureElement}
                onClick={() => onSelect(option)}
              >
                {renderOption ? (
                  renderOption(option, checked)
                ) : (
                  <span className="text-[12px] font-[400] text-[#4B4B4B]">{getLabel(option)}</span>
                )}
                {checked && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4 text-[#0C8DBF]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
